import { Link } from 'react-router-dom';
import type { CardView } from '../../api';
import { isEventPayload, normaliseEventPayload, type EventPayload } from '../../eventsApi';
import { formatLocalDayShort, localDayParts } from '../../format';
import { useSurfaceOpen } from '../cards';
import { useOpenDetail } from '../detail-link';
import { EventStatusBadges, placeLine, startTime } from './EventCard';
import './events.css';

export const BRIEFING_EVENTS_MAX = 3;

/**
 * The briefing's small "events" section: at most three picks, each with the
 * reason it was chosen ("in 2 Tagen", "neu angekündigt"), and a way to the full
 * agenda. The facts come from the card version the edition published, so an
 * edition keeps showing what it showed; the live state is in the agenda.
 */
export function BriefingEvents({ cards }: { cards: CardView[] }) {
  const picks = cards.filter((c) => isEventPayload(c.version.payload)).slice(0, BRIEFING_EVENTS_MAX);
  return (
    <section className="panel briefing-events" aria-labelledby="briefing-events-h">
      <span className="eyebrow">Veranstaltungen</span>
      <h3 id="briefing-events-h" className="briefing-events-title">
        Demnächst in der Agenda
      </h3>
      {picks.length === 0 ? (
        <p className="muted small">In dieser Ausgabe ist kein Termin ausgewählt.</p>
      ) : (
        <ul className="plain briefing-event-list">
          {picks.map((cv) => (
            <BriefingEventRow key={cv.version.id} view={cv} />
          ))}
        </ul>
      )}
      <Link className="text-button" to="/events">
        Ganze Agenda ↗
      </Link>
    </section>
  );
}

function BriefingEventRow({ view }: { view: CardView }) {
  const open = useOpenDetail();
  const onSurface = useSurfaceOpen(view.version.id);
  const p = normaliseEventPayload(view.version.payload as unknown as EventPayload);
  const parts = localDayParts(p.local_date);
  const time = startTime(p);
  const place = placeLine(p);
  const reason = view.entry?.reason;
  return (
    <li className="briefing-event" data-version={view.version.id} onClick={onSurface}>
      {parts && (
        <div className="calendar-date" aria-hidden="true">
          <small>{parts.month}</small>
          <b>{parts.day}</b>
        </div>
      )}
      <div className="briefing-event-body">
        <strong className="briefing-event-title">
          <button type="button" className="title-button" lang={p.language || undefined} onClick={(e) => open(view.version.id, e.currentTarget)}>
            {p.title || view.version.title}
          </button>
        </strong>
        <span className="briefing-event-when">
          {formatLocalDayShort(p.local_date)} · {time ?? 'Zeit offen'}
          {place ? ` · ${place}` : ''}
        </span>
        <span className="meta">
          {reason && <span className="tag">{reason}</span>}
          <EventStatusBadges facts={p} />
          <span>{p.source_name}</span>
        </span>
      </div>
    </li>
  );
}
